import { useState } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { sxCard, tokens } from '../../theme-next';
import { AgencyOrganization } from './hooks/useAgencyProfile';

/**
 * Assistant de changement d'entité juridique.
 *
 * Le premier écran ne demande PAS un SIRET, il demande de quel événement il
 * s'agit : c'est le seul choix qui compte. Même exploitant, on conserve le
 * compte et l'historique. Nouvel exploitant, il faut un client distinct, sinon
 * le repreneur hérite des dossiers et des clients du cédant. Trier sur l'email,
 * comme aujourd'hui, conduit à mélanger les deux.
 */

export interface CompanyLookup {
  siret: string;
  siren: string;
  name: string;
  address: string;
  postCode: string;
  town: string;
  legalFormCode: string | null;
  creationDate: string | null;
  isActive: boolean;
  isHeadOffice: boolean;
}

export interface EntityChangeOutcome {
  changeId: number | null;
  dryRun?: boolean;
  pipedriveUpdated: boolean;
  qontoUpdated: boolean;
  qontoClientId: string | null;
  propagationErrors: string[];
}

const STEPS = ['Nature du changement', 'Nouvelle entité', 'Confirmation'];

const REASONS = [
  { value: 'legal_form_change', label: "Changement de forme juridique (EI vers société, agent co vers agence)" },
  { value: 'establishment_transfer', label: "Transfert d'établissement (même SIREN, nouvelle adresse)" },
  { value: 'correction', label: 'Correction de fiche (SIRET ou raison sociale erronés)' },
];

/** Ligne de comparaison avant/après. Grisée quand la valeur ne bouge pas. */
const DiffRow = ({ label, before, after }: { label: string; before: string | null; after: string }) => {
  const changed = (before ?? '').trim().toLowerCase() !== after.trim().toLowerCase();
  return (
    <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 2, borderBottom: `0.5px solid ${tokens.color.border}` }}>
      <Typography variant="overline" sx={{ width: 110, color: tokens.color.textFaint, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1, color: changed ? tokens.color.textFaint : undefined, textDecoration: changed ? 'line-through' : undefined }}>
        {before || '—'}
      </Typography>
      <ArrowForwardIcon sx={{ fontSize: 16, color: changed ? tokens.color.yellow : tokens.color.border }} />
      <Typography variant="body2" sx={{ flex: 1, fontWeight: changed ? 700 : 400 }}>
        {after || '—'}
      </Typography>
    </Stack>
  );
};

const ChoiceCard = ({
  selected,
  onSelect,
  title,
  consequences,
  disabled,
  disabledNote,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  consequences: string[];
  disabled?: boolean;
  disabledNote?: string;
}) => (
  <Box
    onClick={disabled ? undefined : onSelect}
    sx={{
      ...sxCard,
      p: 5,
      flex: 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      borderColor: selected ? tokens.color.yellow : tokens.color.border,
      borderWidth: selected ? 2 : 1,
      borderStyle: 'solid',
    }}
  >
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Radio checked={selected} disabled={disabled} size="small" sx={{ p: 0, mt: '2px' }} />
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Box component="ul" sx={{ pl: 4, mt: 2, mb: 0 }}>
          {consequences.map((line) => (
            <Typography component="li" variant="body2" key={line} sx={{ color: tokens.color.textFaint }}>
              {line}
            </Typography>
          ))}
        </Box>
        {disabled && disabledNote && (
          <Typography variant="caption" sx={{ display: 'block', mt: 3, color: tokens.color.orange }}>
            {disabledNote}
          </Typography>
        )}
      </Box>
    </Stack>
  </Box>
);

interface EntityChangeDialogProps {
  open: boolean;
  organization: AgencyOrganization;
  onClose: () => void;
  onApplied: () => void;
}

const EntityChangeDialog = ({ open, organization, onClose, onApplied }: EntityChangeDialogProps) => {
  const [step, setStep] = useState(0);
  const [continuity, setContinuity] = useState(true);
  const [siretInput, setSiretInput] = useState('');
  const [lookup, setLookup] = useState<CompanyLookup | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('legal_form_change');
  const [isApplying, setIsApplying] = useState(false);
  const [isDryRun, setIsDryRun] = useState(false);
  const [outcome, setOutcome] = useState<EntityChangeOutcome | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const runLookup = async () => {
    setIsLookingUp(true);
    setLookupError(null);
    setLookup(null);
    try {
      const response = await makeAuthenticatedApiCall<{ success: boolean; data: CompanyLookup; error?: string }>(
        'get',
        `/api/agency-profile/company-lookup?siret=${encodeURIComponent(siretInput)}`,
      );
      setLookup(response.data.data);
      // La date de création de l'établissement est la date d'effet la plus
      // probable : c'est celle du Kbis. Proposée, jamais imposée.
      if (response.data.data.creationDate) setEffectiveDate(response.data.data.creationDate);
    } catch (error: any) {
      setLookupError(error?.response?.data?.error ?? 'Recherche impossible.');
    } finally {
      setIsLookingUp(false);
    }
  };

  const apply = async () => {
    setIsApplying(true);
    setApplyError(null);
    try {
      const response = await makeAuthenticatedApiCall<{ success: boolean; data: EntityChangeOutcome }>(
        'post',
        `/api/agency-profile/${organization.id}/entity-change`,
        { siret: lookup?.siret, effectiveDate, reason, dryRun: isDryRun },
      );
      setOutcome(response.data.data);
      onApplied();
    } catch (error: any) {
      setApplyError(error?.response?.data?.error ?? 'Le changement a échoué.');
    } finally {
      setIsApplying(false);
    }
  };

  const close = () => {
    setStep(0);
    setSiretInput('');
    setLookup(null);
    setLookupError(null);
    setOutcome(null);
    setApplyError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Changement d'entité — {organization.name}</DialogTitle>
      <DialogContent dividers>
        {!outcome && (
          <Stepper activeStep={step} sx={{ mb: 6 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {outcome ? (
          <Box>
            {/* Une propagation partielle ne doit pas s'annoncer comme un succès :
                l'opérateur repartirait en croyant Qonto à jour. */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
              {outcome.dryRun || outcome.propagationErrors.length > 0 ? (
                <ErrorOutlineIcon sx={{ color: tokens.color.orange }} />
              ) : (
                <CheckCircleIcon sx={{ color: tokens.color.green }} />
              )}
              <Typography variant="h6">
                {outcome.dryRun
                  ? 'Simulation : rien n’a été modifié'
                  : outcome.propagationErrors.length > 0
                    ? 'Bascule partiellement appliquée'
                    : 'Bascule enregistrée'}
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Typography variant="body2">
                Pipedrive : {outcome.dryRun ? 'non touché (simulation)' : outcome.pipedriveUpdated ? 'mis à jour' : 'non mis à jour'}
              </Typography>
              <Typography variant="body2">
                Client Qonto {outcome.qontoClientId ?? '(aucun)'} : {outcome.qontoUpdated ? 'mis à jour' : 'non mis à jour'}
              </Typography>
              {outcome.propagationErrors.length > 0 && (
                <Box sx={{ ...sxCard, borderLeft: `3px solid ${tokens.color.orange}`, backgroundColor: tokens.color.orangeLight, p: 4 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                    Bascule partielle, à reprendre à la main :
                  </Typography>
                  {outcome.propagationErrors.map((err) => (
                    <Typography variant="body2" key={err}>
                      {err}
                    </Typography>
                  ))}
                </Box>
              )}
            </Stack>
          </Box>
        ) : step === 0 ? (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
            <ChoiceCard
              selected={continuity}
              onSelect={() => setContinuity(true)}
              title="Même exploitant"
              consequences={[
                'Changement de forme, transfert, correction de fiche',
                "L'agence garde son compte, ses identifiants et ses dossiers",
                'La fiche et le client Qonto sont mis à jour sur place',
              ]}
            />
            <ChoiceCard
              selected={!continuity}
              onSelect={() => setContinuity(false)}
              disabled
              disabledNote="Parcours non encore disponible : il exige de libérer l'email côté Firebase Auth."
              title="Nouvel exploitant"
              consequences={[
                'Revente de licence ou de fonds de commerce',
                "Le repreneur NE DOIT PAS hériter des dossiers du cédant",
                'Nouveau compte, nouveau client de facturation',
              ]}
            />
          </Stack>
        ) : step === 1 ? (
          <Box>
            <Typography variant="body2" sx={{ color: tokens.color.textFaint, mb: 4 }}>
              Saisissez le SIRET figurant sur le Kbis. L'identité est récupérée au registre officiel, ce qui évite de
              recopier une raison sociale ou une adresse à la main.
            </Typography>
            <Stack direction="row" spacing={3} alignItems="flex-start">
              <TextField
                label="SIRET (14 chiffres)"
                value={siretInput}
                onChange={(event) => setSiretInput(event.target.value)}
                error={Boolean(lookupError)}
                helperText={lookupError ?? ' '}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                onClick={runLookup}
                disabled={isLookingUp || siretInput.trim() === ''}
                startIcon={isLookingUp ? <CircularProgress size={14} /> : <SearchIcon />}
                sx={{ mt: '6px' }}
              >
                Rechercher
              </Button>
            </Stack>

            {lookup && (
              <Box sx={{ mt: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Typography variant="h6">{lookup.name}</Typography>
                  <Chip
                    label={lookup.isActive ? 'établissement actif' : 'établissement fermé'}
                    size="small"
                    color={lookup.isActive ? 'success' : 'error'}
                  />
                  {lookup.isHeadOffice && <Chip label="siège" size="small" />}
                </Stack>
                <Box sx={{ ...sxCard, p: 4 }}>
                  <DiffRow label="Raison sociale" before={organization.name} after={lookup.name} />
                  <DiffRow label="SIRET" before={organization.siret} after={lookup.siret} />
                  <DiffRow label="Adresse" before={organization.address} after={lookup.address} />
                  <DiffRow label="Code postal" before={organization.postCode} after={lookup.postCode} />
                  <DiffRow label="Ville" before={organization.town} after={lookup.town} />
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Stack direction="row" spacing={4} sx={{ mb: 5 }}>
              <TextField
                label="Date d'effet"
                type="date"
                value={effectiveDate}
                onChange={(event) => setEffectiveDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Celle du Kbis"
                sx={{ flex: 1, minWidth: 190 }}
              />
              <TextField
                label="Motif"
                select
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                sx={{ flex: 2 }}
              >
                {REASONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Typography variant="overline" sx={{ color: tokens.color.textFaint }}>
              Ce qui va être écrit
            </Typography>
            <Box component="ul" sx={{ pl: 4, mt: 2 }}>
              <Typography component="li" variant="body2">
                La fiche Pipedrive de l'organisation {organization.id} (raison sociale, SIRET, adresse)
              </Typography>
              <Typography component="li" variant="body2">
                Le client Qonto existant, modifié sur place pour conserver sa cadence et son rattachement franchise
              </Typography>
              <Typography component="li" variant="body2">
                Le journal des changements d'entité, avec la date d'effet
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 4, color: tokens.color.textFaint }}>
              Les dossiers, le compte de connexion et les identifiants de l'agence ne sont pas touchés.
            </Typography>

            {/* L'environnement de dev pointe sur le Pipedrive de PROD : la
                simulation permet d'essayer le parcours sans modifier de fiche. */}
            <FormControlLabel
              sx={{ mt: 3 }}
              control={<Checkbox checked={isDryRun} onChange={(event) => setIsDryRun(event.target.checked)} />}
              label={
                <Typography variant="body2">
                  Simulation : afficher ce qui serait écrit, sans rien modifier
                </Typography>
              }
            />

            {applyError && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 4, color: tokens.color.red }}>
                <ErrorOutlineIcon fontSize="small" />
                <Typography variant="body2">{applyError}</Typography>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {outcome ? (
          <Button variant="contained" onClick={close}>
            Fermer
          </Button>
        ) : (
          <>
            <Button onClick={close}>Annuler</Button>
            {step > 0 && <Button onClick={() => setStep(step - 1)}>Retour</Button>}
            {step < 2 ? (
              <Button
                variant="contained"
                onClick={() => setStep(step + 1)}
                disabled={step === 0 ? !continuity : !lookup || !lookup.isActive}
              >
                Continuer
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={apply}
                disabled={isApplying || !effectiveDate}
                startIcon={isApplying ? <CircularProgress size={14} /> : undefined}
              >
                {isDryRun ? 'Lancer la simulation' : 'Appliquer le changement'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EntityChangeDialog;
